import ConsolidationSection from './components/ConsolidationSection';
import FontCover from './components/pages/FontCover';
import { DownloadIcon } from '@/assets/svg';
import { urlLabel } from '@/enum/PermissionEnum';
import { API_ROOT } from '@/module/common';
import CRConsolidationReportViewerDownload from '@/module/coporate-consolidation-report-viewer-download';
import { CONSOLIDATION_API } from '@/module/corporate-consolidation-report/constants';
import { normalizeConsolidationData } from '@/new-components/CRConsolidationReportPDF/constants';
import customToast from '@/new-components/CustomNotification';
import { LoadingOutlined } from '@ant-design/icons';
import {
  Box,
  CircularProgress,
  IconButton,
  Typography,
} from '@material-ui/core';
import { get } from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

const CRConsolidationReportHTML = ({ accessToken, projectId }) => {
  const query = new URLSearchParams(useLocation().search);
  const [loading, setLoading] = useState(true);
  const [consolidationData, setConsolidationData] = useState(null);
  const [downloading, setDownloading] = useState({});
  const projectID = projectId || query.get('projectId');
  const token = accessToken || localStorage.getItem('accessToken');
  const history = useHistory();

  useEffect(() => {
    if (!token || !projectID) {
      window.location.reload();

      history.push(
        `/${urlLabel.previewDownloadCRReport}?requestKey=${localStorage.getItem(
          'requestKey'
        )}&hrEmail=${localStorage.getItem('hrEmail')}`
      );
    }
    setLoading(true);
    fetch(
      `${API_ROOT}${
        CONSOLIDATION_API.data
      }/${projectID}?type=${encodeURIComponent('Consolidated')}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      }
    )
      .then((res) => res.json())
      .then((res) => {
        setLoading(false);
        if (get(res, 'statusCode') === 200) {
          setConsolidationData(res?.data);
        } else {
          customToast(
            'error',
            res?.message || 'Failed to load consolidation report'
          );
          setConsolidationData(null);
        }
      })
      .catch(() => {
        setLoading(false);
        customToast('error', 'Failed to load consolidation report');
        setConsolidationData(null);
      });
  }, [history, projectID, token]);

  const downloadKey = 'pdf';
  const handleDownloadPdf = () => {
    if (downloading[downloadKey]) return;
    setDownloading((prev) => ({ ...prev, [downloadKey]: true }));
  };

  const normalized = useMemo(
    () =>
      typeof normalizeConsolidationData === 'function'
        ? normalizeConsolidationData(consolidationData)
        : consolidationData,
    [consolidationData]
  );

  const projectInfo = normalized?.projectInfo || null;
  const items = Array.isArray(normalized?.items) ? normalized.items : [];

  if (loading) {
    return (
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        height="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box bgcolor="#f5f5f5" minHeight="100vh">
      <Box maxWidth={1120} margin="0 auto">
        <Box
          position="fixed"
          top={16}
          right={16}
          zIndex={99}
          width={60}
          height={60}
          bgcolor="#fff"
          borderRadius={100}
          display="flex"
          justifyContent="center"
          alignItems="center"
          boxShadow="0 1px 6px rgba(0,0,0,0.12)"
        >
          <IconButton
            onClick={handleDownloadPdf}
            disabled={!!downloading[downloadKey]}
          >
            {downloading[downloadKey] ? (
              <LoadingOutlined style={{ fontSize: 24, color: '#6c6c6c' }} />
            ) : (
              <DownloadIcon width={60} height={60} />
            )}
          </IconButton>
        </Box>
        {!projectID ? (
          <Box mt={2} pt={3}>
            <Typography variant="body2" color="textSecondary">
              Missing `projectId` in URL.
            </Typography>
          </Box>
        ) : projectInfo ? (
          <Box mb={3}>
            <FontCover consolidationData={{ projectInfo }} />
          </Box>
        ) : null}

        {Object.keys(downloading).length > 0 && (
          <CRConsolidationReportViewerDownload
            downloading={downloading}
            resetData={() => setDownloading({})}
            consolidationDataOverride={consolidationData}
          />
        )}

        {!items.length ? (
          <Box mt={2}>
            <Typography variant="body2" color="textSecondary">
              No consolidation report items to preview.
            </Typography>
          </Box>
        ) : (
          <>
            <Box mt={3} display="flex" flexDirection="column" gridGap={24}>
              {items.map((it, idx) => (
                <Box key={idx}>
                  <ConsolidationSection id={`consolidation-${idx}`} item={it} />
                </Box>
              ))}
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
};

export default CRConsolidationReportHTML;
